/**
 * ============================================================================
 * ClinIA Perú - Guías nutricionales en la consulta
 * ============================================================================
 * Va debajo de "Indicaciones Nutricionales". El médico elige una de sus guías
 * y puede:
 *   - insertar su resumen corto en el campo (lo que sale en la receta)
 *   - ver / imprimir el PDF completo con su hoja membretada y firma
 *   - enviarlo al correo del paciente; si el paciente no tenía correo, se
 *     guarda en su ficha
 *
 * Las guías se crean y editan en Configuración → Guías nutricionales.
 * ============================================================================
 */

import React, { useEffect, useState } from 'react';
import { Apple, FileText, Mail, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';

const errorText = (error, fallback) => error?.response?.data?.detail || fallback;

export default function NutritionGuidePicker({ patient, consultationId, onInsertSummary, disabled }) {
  const [guides, setGuides] = useState([]);
  const [guideId, setGuideId] = useState('');
  const [busy, setBusy] = useState('');
  const [mailOpen, setMailOpen] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    api.get('/nutrition-guides')
      .then(({ data }) => setGuides(data || []))
      .catch(() => setGuides([]));
  }, []);

  if (!guides.length) return null;

  const guide = guides.find((g) => g.id === guideId);
  const delivery = { patient_id: patient?.id, consultation_id: consultationId || null };

  const openPdf = async () => {
    if (!patient) return toast.error('Seleccione un paciente');
    // La pestaña se abre antes del await para que el navegador no la bloquee
    const tab = window.open('', '_blank');
    setBusy('pdf');
    try {
      const { data } = await api.post(`/nutrition-guides/${guide.id}/pdf`, delivery, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      if (tab) tab.location.href = url; else window.open(url, '_blank');
    } catch (error) {
      if (tab) tab.close();
      toast.error('No se pudo generar el PDF');
    } finally {
      setBusy('');
    }
  };

  const openMail = () => {
    if (!patient) return toast.error('Seleccione un paciente');
    setEmail(patient.email || '');
    setMailOpen(true);
  };

  const sendMail = async () => {
    setBusy('mail');
    try {
      const { data } = await api.post(`/nutrition-guides/${guide.id}/send`, { ...delivery, email, save_email: true });
      toast.success(data.email_saved ? `${data.message}. Correo guardado en la ficha del paciente` : data.message);
      if (data.email_saved && patient) patient.email = email;
      setMailOpen(false);
    } catch (error) {
      toast.error(errorText(error, 'No se pudo enviar el correo'));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="mt-3 p-3 rounded-lg border border-green-200 bg-green-50/50">
      <div className="flex flex-wrap items-center gap-2">
        <Apple className="w-4 h-4 text-green-700" />
        <span className="text-sm font-medium text-green-800">Guía nutricional</span>
        <select
          className="h-8 rounded-md border border-green-200 bg-white px-2 text-sm flex-1 min-w-[200px]"
          value={guideId}
          onChange={(e) => setGuideId(e.target.value)}
        >
          <option value="">Elegir guía…</option>
          {guides.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
        </select>
      </div>

      {guide && (
        <div className="flex flex-wrap gap-2 mt-2">
          {guide.summary && !disabled && (
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => onInsertSummary(guide.summary)}>
              <Plus className="w-3 h-3 mr-1" /> Insertar resumen en la receta
            </Button>
          )}
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs"
            onClick={openPdf} disabled={busy === 'pdf'}>
            {busy === 'pdf' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}
            Ver / imprimir PDF
          </Button>
          <Button type="button" size="sm" className="h-8 text-xs bg-green-600 hover:bg-green-700"
            onClick={openMail}>
            <Mail className="w-3 h-3 mr-1" /> Enviar por correo
          </Button>
        </div>
      )}

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar guía por correo</DialogTitle>
            <DialogDescription>{guide?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="guide-email">Correo del paciente</Label>
            <Input id="guide-email" type="email" value={email} placeholder="paciente@correo.com"
              onChange={(e) => setEmail(e.target.value)} />
            {!patient?.email && (
              <p className="text-xs text-muted-foreground">El paciente no tenía correo: se guardará en su ficha.</p>
            )}
            <p className="text-xs text-muted-foreground">Recibirá la guía en el cuerpo del correo y el PDF adjunto.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMailOpen(false)}>Cancelar</Button>
            <Button onClick={sendMail} disabled={!email || busy === 'mail'} className="bg-green-600 hover:bg-green-700">
              {busy === 'mail' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
